import React, { useMemo, useState } from "react";
import {
	buildGoogleMapsLink,
	buildStaticMapUrl,
	formatCoordinates,
	normalizeLocationImageSrc,
} from "../../helpers/locationMap";

function IconPin({ className = "size-5" }) {
	return (
		<svg
			className={className}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="2"
			aria-hidden
		>
			<path d="M12 21s7-4.35 7-10a7 7 0 1 0-14 0c0 5.65 7 10 7 10z" />
			<circle cx="12" cy="11" r="2.5" />
		</svg>
	);
}

function LocationPreview({
	latitude,
	longitude,
	image,
	link,
	description,
	clientUrl,
}) {
	const [mapFailed, setMapFailed] = useState(false);

	const lat = Number(latitude);
	const lng = Number(longitude);
	const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);

	const mapsLink = useMemo(() => {
		if (link) return link;
		if (!hasCoords) return "";
		return buildGoogleMapsLink(lat, lng);
	}, [link, hasCoords, lat, lng]);

	const staticMapUrl = useMemo(() => {
		if (!hasCoords) return "";
		return buildStaticMapUrl(lat, lng, { width: 320, height: 160, zoom: 16 });
	}, [hasCoords, lat, lng]);

	const storedImage = useMemo(() => normalizeLocationImageSrc(image), [image]);

	const mapSrc = useMemo(() => {
		if (mapFailed) return null;
		if (storedImage) return storedImage;
		return staticMapUrl || null;
	}, [mapFailed, storedImage, staticMapUrl]);

	const coordsLabel = hasCoords ? formatCoordinates(lat, lng) : "";
	const lines = String(description || "")
		.split("\n")
		.map((l) => l.trim())
		.filter(Boolean);
	const title = lines[0] || "Localização compartilhada";
	const subtitle = lines[1] || coordsLabel;

	const openMaps = () => {
		const target = clientUrl || mapsLink;
		if (target) window.open(target, "_blank", "noopener,noreferrer");
	};

	return (
		<div
			className="overflow-hidden rounded-2xl border border-default-200 bg-white shadow-sm dark:border-default-700 dark:bg-default-900"
			style={{ width: "min(100%, 280px)" }}
		>
			<button
				type="button"
				onClick={openMaps}
				disabled={!mapsLink && !clientUrl}
				className="group relative block w-full border-0 bg-default-100 p-0 text-left disabled:cursor-default dark:bg-default-800"
				style={{ minHeight: 140 }}
			>
				{mapSrc ? (
					<img
						src={mapSrc}
						alt="Mapa da localização"
						className="block h-[140px] w-full object-cover"
						onError={() => setMapFailed(true)}
						loading="lazy"
					/>
				) : (
					<div className="flex h-[140px] w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-sky-50 to-emerald-50 text-primary dark:from-default-800 dark:to-default-900">
						<span className="inline-flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary">
							<IconPin className="size-6" />
						</span>
						<span className="px-3 text-center text-xs font-medium text-default-600 dark:text-default-300">
							Visualizar no mapa
						</span>
					</div>
				)}

				{hasCoords && mapSrc && !mapFailed ? (
					<span className="absolute bottom-2 end-2 inline-flex size-8 items-center justify-center rounded-full bg-white/95 text-red-500 shadow-md dark:bg-default-900/95">
						<IconPin className="size-4" />
					</span>
				) : null}
			</button>

			<div className="space-y-1 px-3 py-2.5">
				<p className="m-0 truncate text-sm font-semibold text-default-800 dark:text-default-100">
					{title}
				</p>
				{subtitle ? (
					<p className="m-0 truncate text-xs text-default-500 dark:text-default-400">
						{subtitle}
					</p>
				) : null}
				{(mapsLink || clientUrl) && (
					<button
						type="button"
						onClick={openMaps}
						className="mt-1 border-0 bg-transparent p-0 text-xs font-medium text-primary hover:underline"
					>
						Abrir no Google Maps
					</button>
				)}
			</div>
		</div>
	);
}

export default LocationPreview;
